import { ArrowBackIcon, PhoneIcon, SearchIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  FlexProps,
  Grid,
  GridProps,
  Highlight,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Skeleton,
  Text,
  TextProps,
  Wrap,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import React from "react";
import { useInput } from "rooks";
import { Messages } from "../../core/api";
import { ListedBotAgent } from "../../modules/bot-agent/bot-agent.types";
import { WorkflowInputForm } from "../../modules/workflow/components/WorkflowForm";
import useApi from "../hooks/useApi";
import { useGlobalWorkflowRunner } from "../hooks/useGlobalWorkflowRunner";
import { camelCaseToTitleCase } from "../utils/string.utils";
import ErrorPage from "./ErrorPage";

type SubmittedBotAgent = {
  type: string;
  phoneNumber: string;
  input: Record<string, unknown>;
};

export default function DialerBotAgentForm(props: {
  isDialing: boolean;
  onDial: (botAgent: SubmittedBotAgent) => void;
}) {
  const [selectedBotAgent, setSelectedBotAgent] = React.useState<ListedBotAgent | null>(null);

  return selectedBotAgent ? (
    <DialerBotAgentSelected
      botAgent={selectedBotAgent}
      isDialing={props.isDialing}
      onClickGoBack={() => setSelectedBotAgent(null)}
      onDial={props.onDial}
    />
  ) : (
    <DialerBotAgentSelection onSelect={setSelectedBotAgent} />
  );
}

function DialerBotAgentSelection(props: { onSelect: (botAgent: ListedBotAgent) => void }) {
  const search = useInput();
  const { queries } = useApi();
  const query = useQuery(queries.botAgent.list());

  const filterBotAgents = (botAgents: ListedBotAgent[]) => {
    const searchValue = search.value.toLowerCase();

    return botAgents.filter((botAgent) => {
      return camelCaseToTitleCase(botAgent.type).toLowerCase().includes(searchValue);
    });
  };

  return (
    <Box>
      <InputGroup size="lg">
        <InputLeftElement pointerEvents="none">
          <SearchIcon color="gray.500" />
        </InputLeftElement>
        <Input placeholder="Search bot agent" {...search} />
      </InputGroup>

      {(() => {
        switch (query.status) {
          case "pending":
            return (
              <Cards>
                <Skeleton h="63.5px" rounded="md" />
                <Skeleton h="63.5px" rounded="md" />
                <Skeleton h="63.5px" rounded="md" />
                <Skeleton h="63.5px" rounded="md" />
              </Cards>
            );
          case "error":
            return <ErrorPage error={query.error} resetErrorBoundary={query.refetch} />;
          case "success":
            return (
              <Cards>
                {filterBotAgents(query.data.botAgents).map((botAgent) => (
                  <Card key={botAgent.type} onClick={() => props.onSelect(botAgent)}>
                    <Title>
                      <Highlight query={search.value} styles={{ p: 0, bg: "blue.100" }}>
                        {camelCaseToTitleCase(botAgent.type)}
                      </Highlight>
                    </Title>

                    <Description>{botAgent.description}</Description>
                  </Card>
                ))}
              </Cards>
            );
        }
      })()}
    </Box>
  );
}

function DialerBotAgentSelected(props: {
  botAgent: ListedBotAgent;
  isDialing: boolean;
  onDial: (botAgent: SubmittedBotAgent) => void;
  onClickGoBack: () => void;
}) {
  const { hints } = useGlobalWorkflowRunner();

  const fields = React.useMemo((): Map<string, Messages["WorkflowInput"]> => {
    return new Map(
      Object.entries(props.botAgent.input ?? {}).map(([key, field]) => [
        key,
        { ...field, name: camelCaseToTitleCase(key) },
      ])
    );
  }, [props.botAgent.input]);

  return (
    <Box>
      <Wrap mb="4">
        <IconButton
          aria-label="Go back"
          icon={<ArrowBackIcon fontSize="xl" />}
          rounded="full"
          onClick={props.onClickGoBack}
        />
        <Text alignItems="center" display="flex" fontSize="md" justifyContent="center">
          Bot Agents
          <Text as="span" color="gray.400" px={2}>
            &raquo;
          </Text>
          <Text as="span" color="blue.500">
            {camelCaseToTitleCase(props.botAgent.type)}
          </Text>
        </Text>
      </Wrap>

      <WorkflowInputForm
        fields={fields}
        fileUploadDestination={{ by: "bot-agent-dialer" }}
        hints={hints}
        renderButton={() => (
          <Button
            colorScheme="blue"
            isLoading={props.isDialing}
            leftIcon={<PhoneIcon />}
            ms="auto"
            type="submit"
            variant="solid"
          >
            Call
          </Button>
        )}
        onSubmit={(input) => props.onDial({ ...props.botAgent, input })}
      />
    </Box>
  );
}

function Title(props: TextProps) {
  return (
    <Text color="black" fontSize="md" fontWeight="bold" textTransform="uppercase" {...props} />
  );
}

function Description(props: TextProps) {
  return <Text color="gray.500" fontSize="md" {...props} />;
}

function Cards(props: GridProps) {
  return <Grid gap={2} my={2} templateColumns="repeat(1, 1fr)" {...props} />;
}

function Card(props: FlexProps) {
  return (
    <Flex
      _focusVisible={{ bg: "blue.50", borderColor: "blue.500", transition: "none" }}
      _hover={{ bg: "blue.50", borderColor: "blue.500", transition: "none" }}
      as="button"
      borderColor="gray.200"
      borderWidth="1px"
      cursor="pointer"
      direction="column"
      gap={1}
      outlineColor="blue.500"
      p={5}
      rounded="md"
      textAlign="start"
      transition="all 0.2s"
      {...props}
    />
  );
}
